import {
  Button,
  DocViewerComponent,
  Icons,
  useToggle,
} from '@commenda/kakashi';
import { ServiceRequestDetails } from '@commenda/types/src/service-request/types';
import { documentName } from '@commenda/utils';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import { LegacyDocumentType } from '@prisma/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { KeyedMutator } from 'swr';
import { CorporationUploadFileModal } from '../../../common/modals/CorporationUploadFileModal';
import DirectorUploadFileModal from '../../../common/modals/DirectorUploadFileModal';
import { viewUploadedFile } from '../../../utils';

type ReviewAndSignDocViewerProps = {
  documentType: LegacyDocumentType;
  reviewDocumentType?: LegacyDocumentType;
  directorId?: number;
  onUploadSuccess?: () => void;
  serviceRequest: ServiceRequestDetails;
  mutate: KeyedMutator<unknown>;
  serviceRequestTaskId: number;
};

export function ReviewAndSignDocViewer({
  documentType,
  reviewDocumentType,
  directorId,
  serviceRequestTaskId,
  onUploadSuccess,
  serviceRequest,
  mutate,
}: ReviewAndSignDocViewerProps) {
  const [file, setFile] = useState(null);
  const [documentLink, setDocumentLink] = useState('');
  const uploadModalVisibility = useToggle();

  const { uploadedDocumentToBeReviewed, signedDocument } = useMemo(() => {
    if (directorId) {
      // For director documents
      const currentDirector =
        serviceRequest?.corporation?.corporationHasDirector?.find(
          (each) => each?.director?.id === directorId
        );

      const uploadedDoc = currentDirector?.documents?.find(
        (each) => each?.documentType === (reviewDocumentType || documentType)
      );

      const signedDoc = currentDirector?.documents?.find(
        (each) => each?.documentType === documentType
      );

      return {
        uploadedDocumentToBeReviewed: uploadedDoc,
        signedDocument: signedDoc?.files?.sort((a, b) => b.id - a.id)?.[0],
      };
    } else {
      // For corporation documents
      const uploadedDoc =
        serviceRequest?.corporation?.corporationDocuments?.find(
          (each) => each?.documentType === (reviewDocumentType || documentType)
        );

      const signedDoc = serviceRequest?.corporation?.corporationDocuments?.find(
        (each) => each?.documentType === documentType
      );

      return {
        uploadedDocumentToBeReviewed: uploadedDoc,
        signedDocument: signedDoc?.files?.sort((a, b) => b.id - a.id)?.[0],
      };
    }
  }, [serviceRequest, documentType, reviewDocumentType, directorId]);

  useEffect(() => {
    let mounted = true;

    const fetchSignedUrl = async () => {
      try {
        const relevantFile = uploadedDocumentToBeReviewed?.files?.sort(
          (a, b) => b.id - a.id
        )?.[0];

        if (relevantFile?.id) {
          const link = await getSignedUrlByFileId(relevantFile.id, null);
          if (mounted) {
            setDocumentLink(link);
          }
        }
      } catch (error) {
        console.error('Error fetching signed URL:', error);
      }
    };

    fetchSignedUrl();

    return () => {
      mounted = false;
    };
  }, [serviceRequest, uploadedDocumentToBeReviewed]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const DocViewerComp = useMemo(() => {
    return (
      <div className="h-[420px] overflow-y-auto">
        <DocViewerComponent uri={documentLink} />
      </div>
    );
  }, [documentLink]);

  return (
    <div className="flex gap-4">
      <div className="flex-2 flex-col gap-2 flex-1">
        <input
          type="file"
          style={{ display: 'none' }}
          ref={fileInputRef}
          accept=".pdf"
          onChange={() => {}}
        />
        <h1>{documentName(documentType)}</h1>

        <div className="flex flex-col p-6 border border-border-primary rounded-xl mt-4">
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-md bg-bg-secondary">
                <span className="text-sm font-medium">1</span>
              </div>
              <span className="text-md font-medium text-text-primary">
                Preview & download document
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  window.open(documentLink);
                }}
              >
                <Icons.Download01 className="w-4" />
                <span>Download</span>
              </Button>
            </div>
          </div>

          <div className="border border-border-primary rounded-xl mt-4">
            {DocViewerComp}
          </div>
        </div>

        {/* Upload */}
        <div className="flex flex-col p-6 border border-border-primary rounded-xl mt-4">
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-md bg-bg-secondary">
                <span className="text-sm font-medium">2</span>
              </div>
              <span className="text-md font-medium text-text-primary">
                Upload signed document
              </span>
            </div>
            <div className="flex items-center gap-2">
              {signedDocument ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => viewUploadedFile(signedDocument.id)}
                >
                  View Upload
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="secondary"
                onClick={uploadModalVisibility.toggle}
              >
                <Icons.Upload01 className="h-4 w-4" />
                {signedDocument ? 'Reupload' : 'Upload'}
              </Button>
            </div>
          </div>
        </div>

        {directorId ? (
          <DirectorUploadFileModal
            serviceRequestTaskId={serviceRequestTaskId}
            serviceRequest={serviceRequest}
            file={file}
            directorId={directorId}
            setFile={setFile}
            isOpen={uploadModalVisibility.visible}
            setIsOpen={uploadModalVisibility.hide}
            onUploadSuccess={async () => {
              if (onUploadSuccess) onUploadSuccess();
              await mutate();
              setFile(null);
            }}
            documentType={documentType}
            heading={`Upload Signed ${documentName(documentType)}`}
          />
        ) : (
          <CorporationUploadFileModal
            file={file}
            serviceRequestTaskId={serviceRequestTaskId}
            serviceRequest={serviceRequest}
            setFile={setFile}
            isOpen={uploadModalVisibility.visible}
            setIsOpen={uploadModalVisibility.hide}
            onUploadSuccess={async () => {
              if (onUploadSuccess) onUploadSuccess();
              await mutate();
              setFile(null);
            }}
            documentType={documentType}
            heading={`Upload Signed ${documentName(documentType)}`}
          />
        )}
      </div>
    </div>
  );
}
